import React, { useEffect, useRef, useState } from "react";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import Logo from "shared/Logo/Logo";
import { Disclosure, Transition } from "@headlessui/react";
import { NavLink, useNavigate } from "react-router-dom";
import { NavItemType } from "./NavigationItem";
import { LOKALAI_MENU, LOKALAI_MENU_Nologin } from "data/navigation";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import SocialsList from "shared/SocialsList/SocialsList";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import LangDropdown from "components/Header/LangDropdown";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store";
import { setAuthModalOpen } from "../../redux/features/authModalSlice";
import {
  ArrowLeftEndOnRectangleIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { logout } from "../../redux/authSlice";
import { toast } from "react-toastify";

export interface NavMobileProps {
  data?: NavItemType[];
  onClickClose?: () => void;
}

const NavMobile: React.FC<NavMobileProps> = ({
  data = LOKALAI_MENU,
  onClickClose,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const menuData = user ? data : LOKALAI_MENU_Nologin;
  const defaultOpenIndex = menuData.findIndex(
    (item) =>
      (user && item.name === "Service History") ||
      (!user && item.name === "For customers")
  );

  const [openIndex, setOpenIndex] = useState<number | null>(
    defaultOpenIndex !== -1 ? defaultOpenIndex : null
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(logout());
    toast.success("Log out successfully");
    navigate("/");
  };

  const _renderMenuChild = (item: NavItemType) => {
    return (
      <ul className="nav-mobile-sub-menu pl-2 pb-1 text-base space-y-4">
        {item.children?.map((i, index) => (
          <Disclosure key={`${i.href || "child"}-${index}`} as="li">
            {({ open }) => (
              <>
                {i.name === "Log in or Sign Up" ? (
                  <div
                    onClick={() => {
                      onClickClose?.();
                      dispatch(setAuthModalOpen(true));
                    }}
                    className="cursor-pointer space-x-3 flex w-full px-4 py-2.5 text-redlight font-medium text-sm rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  >
                    {i.icon &&
                      React.createElement(i.icon, {
                        className: "w-7 h-7 text-red-400 shrink-0",
                      })}
                    <div>
                      <p className="text-sm font-semibold text-white group-hover:text-red-400">
                        {i.name}
                      </p>
                    </div>
                  </div>
                ) : (
                  <NavLink
                    end
                    to={{ pathname: i.href || undefined }}
                    className={({ isActive }) =>
                      `flex space-x-3 items-center px-4 text-sm font-medium rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 mt-0.5 ${
                        isActive
                          ? "text-secondary"
                          : "text-neutral-900 dark:text-neutral-200"
                      }`
                    }
                  >
                    {i.icon &&
                      React.createElement(i.icon, {
                        className: "w-7 h-7 text-red-400 shrink-0",
                      })}
                    <div>
                      <p className="text-sm font-semibold text-white group-hover:text-red-400">
                        {i.name}
                      </p>
                      {i.name && (
                        <p className="text-xs text-gray-400">{i.name}</p>
                      )}
                    </div>
                    {i.children && (
                      <span
                        className="flex-1 flex"
                        onClick={(e) => e.preventDefault()}
                      >
                        <Disclosure.Button
                          as="span"
                          className="py-2.5 flex justify-end flex-1"
                        >
                          <ChevronDownIcon
                            className="ml-2 h-4 w-4 text-neutral-500"
                            aria-hidden="true"
                          />
                        </Disclosure.Button>
                      </span>
                    )}
                  </NavLink>
                )}
                {i.children && (
                  <Transition
                    show={open}
                    enter="transition-all duration-500 ease-out"
                    enterFrom="opacity-0 max-h-0"
                    enterTo="opacity-100 max-h-[1000px]"
                    leave="transition-all duration-500 ease-in"
                    leaveFrom="opacity-100 max-h-[1000px]"
                    leaveTo="opacity-0 max-h-0"
                  >
                    <Disclosure.Panel>{_renderMenuChild(i)}</Disclosure.Panel>
                  </Transition>
                )}
              </>
            )}
          </Disclosure>
        ))}
      </ul>
    );
  };

  const _renderItem = (item: NavItemType, index: number) => {
    const isOpen = openIndex === index;
    const hasChildren = !!item.children;

    return (
      <Disclosure
        key={item.id}
        as="li"
        className="text-neutral-900 dark:text-white"
        defaultOpen={isOpen}
      >
        {({ open }) => (
          <>
            <div onClick={() => setOpenIndex(isOpen ? null : index)}>
              <NavLink
                end
                to={{ pathname: item.href || undefined }}
                className={({ isActive }) =>
                  `flex w-full px-4 font-medium uppercase tracking-wide text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg ${
                    isActive ? "text-secondary" : ""
                  }`
                }
              >
                <span
                  className={`py-2.5 pr-3 ${
                    !item.children ? "block w-full" : ""
                  }`}
                >
                  {item.name}
                </span>
                {item.children && (
                  <span
                    className="flex-1 flex"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Disclosure.Button
                      as="span"
                      className="py-2.5 flex items-center justify-end flex-1"
                    >
                      <ChevronDownIcon
                        className="ml-2 h-4 w-4 text-neutral-500"
                        aria-hidden="true"
                      />
                    </Disclosure.Button>
                  </span>
                )}
              </NavLink>
            </div>
            {hasChildren && (
              <Transition
                show={isOpen}
                enter="transition-all duration-500 ease-out"
                enterFrom="opacity-0 max-h-0"
                enterTo="opacity-100 max-h-[1000px]"
                leave="transition-all duration-500 ease-in"
                leaveFrom="opacity-100 max-h-[1000px]"
                leaveTo="opacity-0 max-h-0"
              >
                <Disclosure.Panel>{_renderMenuChild(item)}</Disclosure.Panel>
              </Transition>
            )}
          </>
        )}
      </Disclosure>
    );
  };

  return (
    <div className="relative h-screen w-full ">
      <span className="absolute right-4 top-4 z-20">
        <ButtonClose onClick={onClickClose} />
      </span>

      <div className="overflow-y-auto w-full h-screen py-2 transition transform shadow-lg ring-1 dark:ring-neutral-700 bg-white dark:bg-neutral-900 divide-y-2 divide-neutral-100 dark:divide-neutral-800 pb-00 gap-y-12">
        <div>
          {" "}
          <div className="py-6 px-5">
            {user ? (
              <div>
                <h2 className="text-xl font-semibold">
                  Welcome back, {user?.displayName}
                </h2>
                <p className="text-sm text-gray-400">
                  You have 2 new messages and 15 new tasks
                </p>
              </div>
            ) : (
              <div>
                <h2 className="text-xl ">
                  Welcome to{" "}
                  <span className="text-redlight font-semibold">Lokalai</span>
                </h2>
                <p className="text-sm text-gray-400">
                  Please login to continue{" "}
                </p>
              </div>
            )}
            {/* <span className="absolute right-2 top-2 p-1">
            <ButtonClose onClick={onClickClose} />
          </span> */}
          </div>
          <ul className="flex flex-col py-6 px-2 space-y-1">
            {menuData.map(_renderItem)}
          </ul>
          <div className="flex items-center justify-between py-6 px-5">
            <a
              className="inline-block"
              href="https://demo.lokalai.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ButtonPrimary className="text-redlight ">
                Demo <ArrowRightIcon className="ml-2 w-4 h-4" />
              </ButtonPrimary>
            </a>
          </div>
          <div className="flex items-center justify-between py-6 px-5">
            <a
              className="inline-block"
              href="https://business.lokalai.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ButtonPrimary>
                For Business <ArrowRightIcon className="ml-2 w-4 h-4" />
              </ButtonPrimary>
            </a>
            <LangDropdown panelClassName="z-10 w-screen max-w-[280px] px-4 mb-3 -right-3 bottom-full sm:px-0" />
          </div>
          {user && (
            <div className="flex items-center justify-between py-6 px-5 ">
              <a
                className="inline-block"
                onClick={handleLogout}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ButtonPrimary>
                  Log out{" "}
                  <ArrowLeftEndOnRectangleIcon className="ml-2 w-4 h-4" />
                </ButtonPrimary>
              </a>
            </div>
          )}
          <div className="h-20 sm:h-32 lg:h-40" />
        </div>
        <div className="py-6 px-5 mb-24">
          <Logo />
          <div className="flex flex-col mt-5 text-neutral-700 dark:text-neutral-300 text-sm">
            <span>Discover our Lokalai in Social Media</span>
            <div className="flex justify-between items-center mt-4">
              <SocialsList itemClass="w-9 h-9 flex items-center justify-center rounded-full bg-neutral-100 text-xl dark:bg-neutral-800 dark:text-neutral-300" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavMobile;
